import { Badge, FuelAlertVariant, Paper, Theme } from "@deere/fuel-react";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, Button, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FileDownloadedNotificationProps } from "./FileDownloadedNotification";
import { dayJSLang } from "../../../../../../../../types";

const FileDownloadedNotificationV2 = ({
  notification,
  expireNotification,
  successMessage,
  failureMessage,
  downloadFunction,
}: FileDownloadedNotificationProps) => {
  const { t, i18n } = useTranslation(["common"]);
  const theme = useTheme();
  const { authState } = useOktaAuth();

  const isFileCompiledSuccessfully =
    notification.payload.compilationStatus === "COMPLETED";
  const notificationTitle = isFileCompiledSuccessfully
    ? t("notifications.download.ready.messageTitle")
    : t("notifications.download.failed.messageTitle");
  const notificationMessage = isFileCompiledSuccessfully
    ? successMessage
    : failureMessage;
  const notificationBadgeProps = getNotificationBadgeProps(
    theme,
    isFileCompiledSuccessfully
  );
  const showDownloadLink =
    authState &&
    (isFileCompiledSuccessfully ||
      notification.payload.compilationStatus === "PARTIAL_SUCCESS") &&
    notification.payload.downloadUrl;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        padding: theme.tokens.FuelSpacing4,
        marginBottom: 0.125,
        ...(!notification.isRead && {
          borderLeft: `4px solid ${theme.tokens.FuelColorAlertInfoIcon}`,
          backgroundColor: theme.tokens.FuelColorAlertInfoBackground,
        }),
      }}
    >
      <Stack
        data-testid={`notification-item-${notification.payload.id}`}
        direction="row"
        spacing={theme.tokens.FuelSpacing4}
      >
        <Box>
          <Badge
            sx={{
              width: "24px",
              height: "24px",
              ".FuelBadge-label": { display: "none" },
            }}
            startIcon={notificationBadgeProps.icon}
            variant={notificationBadgeProps.variant}
          />
        </Box>

        <Box sx={{ width: "100%" }}>
          <Box
            data-testid="notification-content"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.tokens.FuelSpacing1,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {notificationTitle}
            </Typography>
            <Typography variant="body2">{notificationMessage}</Typography>
          </Box>

          <Box
            sx={{
              marginTop: theme.tokens.FuelSpacing4,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {showDownloadLink &&
              (notification.isExpired ? (
                <Button
                  data-testid="download-expired"
                  startIcon={<CheckIcon />}
                  variant="text"
                  disabled
                >
                  {t("notifications.promotionFileDownloadStatus.expired")}
                </Button>
              ) : (
                <Button
                  data-testid="download-link"
                  startIcon={<FileDownloadIcon />}
                  variant="text"
                  onClick={() => {
                    downloadFunction(authState?.accessToken?.accessToken!);
                    if (expireNotification) expireNotification();
                  }}
                >
                  {t(
                    "notifications.promotionFileDownloadSuccessNotificationMessage.downloadLink"
                  )}
                </Button>
              ))}

            <Typography
              data-testid="created-at"
              sx={{
                fontSize: theme.tokens.FuelFontSizeIcon,
                fontWeight: theme.tokens.FuelFontWeightRegular,
                lineHeight: theme.tokens.FuelLineHeightXs,
                marginLeft: "auto",
                color: theme.tokens.FuelColorTextTertiary,
              }}
            >
              {formatDate(notification.createdAt, i18n.language)}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

export default FileDownloadedNotificationV2;

function getNotificationBadgeProps(
  theme: Theme,
  isFileCompiledSuccessfully: boolean
): { icon: ReactNode; variant: FuelAlertVariant } {
  if (isFileCompiledSuccessfully) {
    return {
      icon: (
        <CheckCircleIcon
          data-testid="file-download-successful-icon"
          sx={{
            color: theme.tokens.FuelColorAlertSuccessIcon,
          }}
        />
      ),
      variant: "success",
    };
  }
  return {
    icon: (
      <CancelIcon
        data-testid="file-download-failure-icon"
        sx={{
          color: theme.tokens.FuelColorAlertErrorIcon,
        }}
      />
    ),
    variant: "error",
  };
}

function formatDate(dateString: Date, language: string): string {
  return dayjs(dateString).locale(dayJSLang[language]).format("DD-MMM-YYYY");
}
